<template>
  <main :class="{ 'cursor-wait': pending }">
    
<FormKitLazyProvider config-file="true">
<section class="bg-primary-50 py-30 lg:py-50 mb-30">
      <div class="container">
        <h1
          class="md:text-5xl text-2xl font-bold mb-30 text-primary-500 lg:mb-40"
        >
          {{ node?.title }}
        </h1>

        <FormKit
          type="form"
          :actions="false"
          :form-class="{
            relative: true,
          }"
          @submit.prevent
        >
          <SearchInput
            v-model="searchTerm"
            class="mb-20"
            disable-suggestions
            :search-input-placeholder="
              $texts(
                'governmentResolutions.searchInputPlaceholder',
                'Suchbegriff oder Präsidial-Nr.',
              )
            "
          />

          <span class="font-bold block mb-5 mt-30">
            {{
              $texts('governmentResolutions.filterMeetings', 'Filter Sitzungen')
            }}
          </span>

          <div class="flex flex-col md:flex-row md:gap-10 gap-30 mb-10">
            <!-- From Date Filter -->
            <FormKit
              v-model="meetingDateFrom"
              type="date"
              :label="$texts('governmentResolutions.dateFrom', 'von')"
              inner-class="!w-full"
              :input-class="{
                'w-full': true,
                'my-10': false,
              }"
              :label-class="{
                block: true,
              }"
            />

            <!-- To Date Filter -->
            <FormKit
              v-model="meetingDateTo"
              type="date"
              :label="$texts('governmentResolutions.dateTo', 'bis')"
              inner-class="!w-full"
              :input-class="{
                'w-full': true,
                'my-10': false,
              }"
              :label-class="{
                block: true,
              }"
            />
          </div>
          <div class="text-sm text-primary-500">
            {{ resultCount }}
          </div>
        </FormKit>
      </div>
    </section>

    <Container class="mb-30">
      <section v-if="hitGroups">
        <GovernmentResolutionBusinessGroup
          v-for="hitGroup in hitGroups"
          :key="hitGroup.id"
          class="mb-25"
          :group="hitGroup"
          :back-url="overviewUrl"
          :is-open="isOpen"
        />

        <!--- Pagination -->
        <Pagination
          v-if="searchResponse?.totalPages && searchResponse?.totalPages > 1"
          :page="parseInt(page)"
          :total-pages="searchResponse.totalPages"
          class="my-30"
        />
      </section>
    </Container>
</FormKitLazyProvider>

  </main>
</template>

<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import type {
  GovernmentResolutionSearchResponse,
  GovernmentResolutionsSearchApiParams,
  Hit,
  HitGroup,
  HitGroups,
  MeetingDate,
} from '~/layers/government-resolutions/types'

definePageMeta({
  name: 'government-resolutions-overview',
  drupalFrontendRoute: true,
  languageMapping: {
    de: '/apps/regierungsratsbeschluesse',
  },
})

const nuxtRoute = useRoute()
const { $texts } = useNuxtApp()
const searchTerm = useQueryString('text')
const page = useQueryString('page', '1')
const meetingDateFrom = useQueryString('meeting_date_from', '')
const meetingDateTo = useQueryString('meeting_date_to', '')

// Overview Url which will be used to return to the overview from the detail
// page.
const overviewUrl = computed(() => {
  return nuxtRoute.fullPath
})

// Query Key.
const queryKey = computed(() => {
  const queryKey: string[] = ['GovernmentResolutionsSearch']

  queryKey.push('page=' + page.value)

  if (searchTerm.value.trim().length !== 0) {
    queryKey.push('searchText=' + searchTerm.value.trim().replace(' ', ','))
  }

  if (meetingDateFrom.value !== '') {
    queryKey.push('meetingDateFrom=' + meetingDateFrom.value)
  }

  if (meetingDateTo.value !== '') {
    queryKey.push('meetingDateTo=' + meetingDateTo.value)
  }

  return queryKey.join('::')
})

const { data: searchResponse, pending } =
  await useAsyncData<GovernmentResolutionSearchResponse>(
    queryKey.value,
    async () => {
      const searchQuery: GovernmentResolutionsSearchApiParams = {
        search_text: searchTerm.value,
        page: parseInt(page.value) - 1,
      }

      if (meetingDateFrom.value !== '') {
        searchQuery.meeting_date_from = meetingDateFrom.value
      }

      if (meetingDateTo.value !== '') {
        searchQuery.meeting_date_to = meetingDateTo.value
      }

      return await $fetch<GovernmentResolutionSearchResponse>(
        '/api/government-resolutions/search',
        {
          query: searchQuery,
        },
      )
    },
    {
      watch: [queryKey],
    },
  )

const resultCount = computed<string>(() => {
  const count = searchResponse?.value?.result?.totalHits || 0

  return $texts(
    'governmentResolutions.resultsTitle',
    '@count Resultate',
  ).replace('@count', count.toString())
})

const isOpen = computed(() => {
  const count = searchResponse?.value?.result?.totalHits
  return !!(
    count &&
    count > 0 &&
    (nuxtRoute.query.meeting_date_from ||
      nuxtRoute.query.meeting_date_to ||
      nuxtRoute.query.text)
  )
})

const hitGroups = computed<HitGroup[]>(() => {
  const groups: HitGroups = {}

  let visibleDateRangeFrom: number
  let visibleDateRangeTo: number

  // If response from search is empty, then return no hit groups.
  if (!searchResponse.value) {
    return []
  }

  // Determine the visible range of dates for the current page.
  searchResponse.value.result.hits.forEach((hit: Hit) => {
    // Do not display any hit which as no meeting dates.
    if (hit.meetingDates.length === 0) {
      return
    }

    const earliestMeetingDate: MeetingDate = hit.meetingDates[0]

    // Set initial value for visible date range from.
    if (!visibleDateRangeFrom) {
      visibleDateRangeFrom = earliestMeetingDate.asNumber
    }

    // Set initial value for visible date range to.
    if (!visibleDateRangeTo) {
      visibleDateRangeTo = earliestMeetingDate.asNumber
    }

    // Increase visible date range to if meeting date is newer.
    if (visibleDateRangeTo < earliestMeetingDate.asNumber) {
      visibleDateRangeTo = earliestMeetingDate.asNumber
    }

    // Increase visible date range to if meeting date is older.
    if (earliestMeetingDate.asNumber < visibleDateRangeFrom) {
      visibleDateRangeFrom = earliestMeetingDate.asNumber
    }
  })

  // Traverse every hit and add any hit, which has one or more meeting dates
  // within the visible date range.
  searchResponse.value.result.hits.forEach((hit) => {
    hit.meetingDates.forEach((meetingDate: MeetingDate) => {
      // If meeting date is within the visible date range.
      if (
        visibleDateRangeFrom <= meetingDate.asNumber &&
        meetingDate.asNumber <= visibleDateRangeTo
      ) {
        // Create new hit group if key does not exist yet.
        if (!(meetingDate.asNumber in groups)) {
          groups[meetingDate.asNumber] = {
            id: meetingDate.asNumber,
            label: meetingDate.asString,
            hits: [],
          }
        }

        // Add hit to group.
        groups[meetingDate.asNumber].hits.push(hit)
      }
    })
  })

  const descendingGroupKeys: string[] = Object.keys(groups).sort((a, b) =>
    b.localeCompare(a),
  )

  const descendingGroups: HitGroup[] = []
  descendingGroupKeys.forEach((key: string) => {
    descendingGroups.push(groups[key])
  })

  return descendingGroups
})

const { entity: node } = await useDrupalRouteQuery('page')

await renderPageDependencies()
</script>
